import { useEffect } from 'react';

interface PageMetadataOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

export function usePageMetadata({ title, description, canonicalPath = '/', ogImage }: PageMetadataOptions) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: description,
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: `${window.location.origin}${canonicalPath}`,
    });
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', {
        property: 'og:image',
        content: ogImage,
      });
      upsertMeta('meta[name="twitter:image"]', {
        name: 'twitter:image',
        content: ogImage,
      });
    }
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    });
    upsertCanonical(`${window.location.origin}${canonicalPath}`);

    return () => {
      document.title = previousTitle;
    };
  }, [canonicalPath, description, ogImage, title]);
}
